import { CategoryScale, Chart, Filler, Legend, LinearScale, LineController, LineElement, PointElement, Title, Tooltip } from "chart.js";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const LineChart = ({
  chartId,
  chartData,
  chartTitle,
  aspectRatio,
  labelYAxis,
  unitYAxis,
  percentYAxis,
  legend,
  startYAtZero
}) => {
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    if (chartInstance != null) {
      chartInstance.destroy();
    }

    const myChart = new Chart(chartRef.current, {
      type: "line",
      data: chartData,
      options: {
        animations: {
          x: false
        },
        aspectRatio: Math.round(aspectRatio * 100) / 100,
        plugins: {
          legend: {
            display: legend,
            position: "bottom"
          },
          title: chartTitle
            ? { display: true, text: chartTitle }
            : { display: false },
          tooltip: {
            intersect: false,
            callbacks: {
              label: context => {
                let label = context.dataset.label || "";

                if (label) {
                  label += ": ";
                }

                label += percentYAxis
                  ? `${context.parsed.y * 100}%`
                  : `${context.parsed.y.toLocaleString("en-CA")}`;

                label += unitYAxis ? ` ${unitYAxis}` : "";

                return label;
              }
            }
          }
        },
        responsive: true,
        scales: {
          y: {
            type: "linear",
            display: labelYAxis,
            beginAtZero: startYAtZero,
            scaleLabel: labelYAxis
              ? {
                  display: true,
                  labelString: labelYAxis + (unitYAxis ? ` (${unitYAxis})` : "")
                }
              : {},
            ticks: {
              maxTicksLimit: 6,
              callback: value =>
                percentYAxis
                  ? `${Math.round(value * 100)}%`
                  : value.toLocaleString("en-CA")
            }
          }
        }
      }
    });

    setChartInstance(myChart);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <canvas id={chartId} ref={chartRef} />;
};

LineChart.propTypes = {
  chartId: PropTypes.string.isRequired,
  chartData: PropTypes.objectOf(PropTypes.any),
  chartTitle: PropTypes.string,
  aspectRatio: PropTypes.number,
  labelYAxis: PropTypes.string,
  unitYAxis: PropTypes.string,
  percentYAxis: PropTypes.bool,
  legend: PropTypes.bool,
  startYAtZero: PropTypes.bool
};

LineChart.defaultProps = {
  chartData: {},
  chartTitle: null,
  aspectRatio: 1.5,
  labelYAxis: null,
  unitYAxis: null,
  percentYAxis: false,
  legend: false,
  startYAtZero: false
};

export default LineChart;
